import { FormDialogMode, useNotify } from '@globaleris/ui.library'
import { Button } from '@material-ui/core'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import Page from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { hasRole } from '../../../helpers/permissionsHelper'
import { NonAuthRoutes } from '../../../model'
import { UserRoles } from '../../../model/enums/userRoles'
import { getActiveCompany, getActiveUser } from '../../../redux'
import {
  TaskAddEditForm,
  TaskAddEditFormValidation
} from '../components/taskAddEditForm/taskAddEditForm'
import { useFetchAtlasUsers, useFetchGlobalerisUsers, useGetTaskTypes } from '../hooks/tasks.hooks'
import {
  mapTaskAddFormModelToTaskPostModel,
  mapTaskTypeResponseToSelectOptionArray
} from '../tasks.mapper'
import { TaskAddEditFormModel } from '../tasks.model'
import { createTask } from '../tasks.services'

interface TaskAddPageProps {}

export const MAX_CHAR = 1000

export const TaskAddPage: React.FC<TaskAddPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'tasks', 'enums'])
  const history = useHistory()
  const { info, error } = useNotify()

  const [formData, setFormData] = useState<TaskAddEditFormModel>({} as TaskAddEditFormModel) // related>EntityCode would be added after fetching the taskId
  const formRef = useRef<TaskAddEditFormValidation>(null)

  // TaskTypes
  const { taskTypes, isFetching: isFetchingTaskTypes } = useGetTaskTypes((errorMsg: string) => {
    error(t(errorMsg))
  })

  //User info
  const activeUser = getActiveUser()
  const hasAtlasAdminRole = hasRole(UserRoles.ATLAS_ADMIN)
  const [assignedActiveUser, setAssignedActiveUser] = useState<boolean>(false)
  const {
    globClientUsersOptions,
    globalerisClient,
    isFetching: isFetchingUsers
  } = useFetchGlobalerisUsers(activeUser?.domain, (errorMsg: string) => {
    error(t(errorMsg))
  })
  const { atlasUsersOptions, isFetching: isFetchingAtlasUsers } = useFetchAtlasUsers(
    (errorMsg: string) => {
      error(t(errorMsg))
    },
    hasAtlasAdminRole
  )

  // company info
  const companyId = getActiveCompany()?.id

  // #region Handlers Starts
  const handleAssignUserClick = () => {
    setAssignedActiveUser(true)
  }

  const handleSave = async () => {
    const formHasMissingInputs = formRef.current?.validateForm()
    if (formHasMissingInputs) {
      error(t('tasks:editTask.form.incompleteForm'))
    } else {
      const request = mapTaskAddFormModelToTaskPostModel(formData, companyId!)
      await createTask(request).then(response => {
        if (response.success) {
          info(t('tasks:fetchResponse.saveTask.success'))
          history.push(NonAuthRoutes.Tasks, { forceRefreshFlag: true })
        } else {
          error(t('tasks:fetchResponse.saveTask.error'))
        }
      })
    }
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  const handleFormDataChange = (updatedFormData: TaskAddEditFormModel) => {
    setFormData(updatedFormData)
  }
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('tasks:addTask.title')}>
        <Button
          variant="text"
          color="primary"
          size="small"
          onClick={() => {
            history.goBack()
          }}
        >
          {t('tasks:editTask.actions.back')}
        </Button>
        <Button
          variant="text"
          color="primary"
          size="small"
          disabled={assignedActiveUser}
          onClick={handleAssignUserClick}
        >
          {t('tasks:editTask.actions.assign')}
        </Button>
        <Button variant="contained" color="primary" size="small" onClick={handleSave}>
          {t('tasks:editTask.actions.save')}
        </Button>
      </PageHeader>
      <PageCenterColumn>
        <TaskAddEditForm
          key={JSON.stringify(formData)} // handles rendering issue when formData is updated
          ref={formRef}
          taskData={{ ...formData }}
          formMode={FormDialogMode.ADD}
          maxChar={MAX_CHAR}
          isFetchingUsers={isFetchingUsers}
          globClientUsersOptions={globClientUsersOptions}
          atlasUsersOptions={atlasUsersOptions}
          taskTypes={mapTaskTypeResponseToSelectOptionArray(taskTypes)}
          isFetchingTaskTypes={isFetchingTaskTypes}
          isFetchingAtlasUsers={isFetchingAtlasUsers}
          hasAtlasAdminRole={hasAtlasAdminRole}
          onFormDataChange={handleFormDataChange}
          assignedActiveUser={assignedActiveUser}
        />
      </PageCenterColumn>
    </Page>
  )
}
